import React from 'react';
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  SvgIcon,
  Typography
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Client from '../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatMoney(value) {
  const numberValue = parseFloat(value || 0);

  if (numberValue >= 1000000) {
    return thousandsSeparator((numberValue / 1000000).toFixed(1)) + "M";
  }

  if (numberValue >= 1000) {
    return thousandsSeparator((numberValue / 1000).toFixed(1)) + "K";
  }

  return thousandsSeparator(numberValue.toFixed(0));
}

class OverviewProductSalesMTD extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      data: {},
      filterData: {}
    };
  }

  componentDidMount() {
    let queryData = {};
    let data = BackofficeReportsApi.getProductsSalesMtd(this.state.client, queryData);

    if (data) {
      this.setState({ data: data });
    }

    this.setState({ filterData: queryData });
  }

  render() {
    let productSalesMTD = 0;
    let productSalesLM = 0;
    let difference = 0;
    let dif_positive = false;

    if (this.state.data) {
      if (this.state.data.Product_sales_MTD !== undefined) {
        productSalesMTD = parseFloat(this.state.data.Product_sales_MTD);
      }

      if (this.state.data.Product_sales_LM !== undefined) {
        productSalesLM = parseFloat(this.state.data.Product_sales_LM);
      }

      if (this.state.data.Product_sales_change !== undefined) {
        difference = parseFloat(this.state.data.Product_sales_change);
      } else if (productSalesLM !== 0) {
        difference = parseFloat((productSalesMTD / productSalesLM - 1) * 100);
      } else {
        difference = productSalesMTD > 0 ? 100 : 0;
      }

      dif_positive = difference >= 0;
    }

    const displayValue = formatMoney(productSalesMTD);
    const progressValue =
      productSalesLM > 0
        ? (productSalesMTD / productSalesLM) * 100
        : productSalesMTD > 0
          ? 100
          : 0;
    const safeProgress = Math.max(0, Math.min(progressValue, 100));

    return (
      <Card
        sx={{
          overflow: 'hidden',
          position: 'relative',
          borderRadius: { xs: '14px', sm: '16px' },
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: '#1a237e',
          },
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.5, sm: 1.75, md: 2 },
            '&:last-child': {
              pb: { xs: 1.5, sm: 1.75, md: 2 },
            },
          }}
        >
          <Stack
            sx={{
              height: '100%',
              minHeight: { xs: 104, sm: 118 },
            }}
            justifyContent="space-between"
            spacing={1.25}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  lineHeight: 1.2,
                  color: '#1a237e',
                  fontWeight: 800,
                  letterSpacing: '.015em',
                  fontSize: { xs: '0.74rem', sm: '0.8rem', md: '0.84rem' },
                  textTransform: 'uppercase',
                }}
              >
                Product sales MTD
              </Typography>

              <Typography
                sx={{
                  mt: 0.6,
                  fontWeight: 800,
                  lineHeight: 1,
                  color: '#111827',
                  fontSize: { xs: '2rem', sm: '2.2rem', md: '2.35rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayValue}
              </Typography>
            </Box>

            <Box sx={{ width: '100%' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.6, minWidth: 0 }}
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={0.5}
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.35,
                      px: 0.7,
                      py: 0.25,
                      borderRadius: 999,
                      bgcolor: dif_positive
                        ? 'rgba(46, 125, 50, 0.10)'
                        : 'rgba(211, 47, 47, 0.10)',
                      flexShrink: 0,
                    }}
                  >
                    <SvgIcon
                      color={dif_positive ? 'success' : 'error'}
                      sx={{ fontSize: 17 }}
                    >
                      {dif_positive ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                    </SvgIcon>

                    <Typography
                      color={dif_positive ? 'success.main' : 'error.main'}
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '0.72rem', sm: '0.78rem' },
                        lineHeight: 1.2,
                      }}
                    >
                      {Math.abs(difference).toFixed(1)}%
                    </Typography>
                  </Box>

                  <Typography
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.72rem', sm: '0.78rem' },
                      fontWeight: 700,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                  >
                    vs. last month
                  </Typography>
                </Stack>
              </Stack>

              <LinearProgress
                value={safeProgress}
                variant="determinate"
                sx={{
                  height: 8,
                  borderRadius: 999,
                  bgcolor: 'rgba(26, 35, 126, 0.10)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    bgcolor: '#1a237e',
                  },
                }}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default OverviewProductSalesMTD;
